import { supabase } from '../lib/supabaseClient'
import type {
  PaymentError,
  VerifyTransactionRequest,
  VerifyTransactionResponse,
} from '../types/payment'

/**
 * Payment Service
 * Checkout is started server-side via `services/checkoutService.ts`
 * (`start-checkout`). This file only keeps transaction verification used by
 * the legacy /payment-callback page.
 */

/**
 * Verify a transaction status
 * @param reference - Payment reference from Paystack
 * @returns Promise with transaction verification result
 */
export async function verifyTransaction(
  reference: string,
  context?: { scanId?: string | null; userId?: string | null },
): Promise<VerifyTransactionResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session) {
    throw new Error('No active session. Please log in.')
  }

  const requestBody: VerifyTransactionRequest = {
    reference,
  }
  if (context?.scanId) requestBody.scan_id = context.scanId
  if (context?.userId) requestBody.user_id = context.userId

  const { data, error } = await supabase.functions.invoke(
    'verify-transaction',
    {
      body: requestBody,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    },
  )

  if (error) {
    console.error('Transaction verification error:', error)
    throw new Error(error.message || 'Failed to verify transaction')
  }

  if (!data) {
    throw new Error('No response from verification service')
  }

  if ('error' in data) {
    const paymentError = data as PaymentError
    throw new Error(paymentError.message || paymentError.error)
  }

  return data as VerifyTransactionResponse
}
